import { DestroyRef, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { PAYMENT_METHODS } from '@shared-constants';
import { SelectOption } from '@shared-models';
import { calculateAvailable, valueMatchesRoundedTarget } from '@shared-utils';

import { IPaymentsList } from 'src/app/@interfaces/payments.interface';
import { ITreasury } from 'src/app/@interfaces/treasury.interface';
import { PaymentsService } from 'src/app/@shared/components/dialog/crud-quotation/payments/payments.service';

export interface AdvancePaymentHost {
  advancePayments: Signal<ITreasury[]>;
  useAdvancePayment: Signal<boolean>;
  selectedAdvancePaymentId: WritableSignal<number | null>;
  advanceValueControl: FormControl<string | number | null>;
  paymentValueControl: FormControl<string | number | null>;
  payFullAmount: WritableSignal<boolean>;
  useFullAdvanceAvailable: WritableSignal<boolean>;
  paymentMethodControl: FormControl<number | null>;
  bankOrCashierControl: FormControl<number | null>;
  listCashiers: Signal<IPaymentsList[]>;
  listBanks: Signal<IPaymentsList[]>;
  missingPaid: Signal<number>;
  paymentsService: PaymentsService;
  translate: TranslateService;
}

export class AdvancePaymentController {
  constructor(private readonly host: AdvancePaymentHost) {}

  selectAdvancePayment(idTreasury: number | null): void {
    this.host.selectedAdvancePaymentId.set(idTreasury);

    const payment = this.host
      .advancePayments()
      .find((candidate) => candidate.id_treasury === idTreasury);
    this.host.advanceValueControl.setValue(
      payment ? this.selectedAdvancePaymentAvailable : null,
    );
    this.syncPaymentOriginDisplay(payment?.paymentType.id_payment ?? null);
  }

  get isAdvanceSelected(): boolean {
    return (
      this.host.useAdvancePayment() &&
      this.host.selectedAdvancePaymentId() !== null
    );
  }

  mirrorValueControls(destroyRef: DestroyRef): void {
    this.host.paymentValueControl.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((value) => {
        if (this.isAdvanceSelected) {
          this.host.advanceValueControl.setValue(value, { emitEvent: false });
        }
      });

    this.host.advanceValueControl.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((value) => {
        if (!this.isAdvanceSelected) {
          return;
        }
        this.host.paymentValueControl.setValue(value, { emitEvent: false });
        this.syncFullAdvanceFlags(value);
      });
  }

  syncFullAdvanceFlags(value: string | number | null): void {
    const isFullAdvance =
      this.isAdvanceSelected &&
      valueMatchesRoundedTarget(value, this.selectedAdvancePaymentAvailable);
    this.host.payFullAmount.set(isFullAdvance);
    this.host.useFullAdvanceAvailable.set(isFullAdvance);
  }

  syncPaymentOriginDisplay(idPayment: number | null): void {
    if (idPayment === null) {
      this.host.paymentMethodControl.reset(null);
      this.host.bankOrCashierControl.reset(null);
      return;
    }

    const isCashier = this.host
      .listCashiers()
      .some((cashier) => cashier.id_payment === idPayment);
    this.host.paymentMethodControl.setValue(isCashier ? 1 : 2);
    this.host.bankOrCashierControl.setValue(idPayment);
  }

  get selectedAdvancePaymentAvailable(): number {
    const payment = this.host
      .advancePayments()
      .find(
        (candidate) =>
          candidate.id_treasury === this.host.selectedAdvancePaymentId(),
      );

    return payment
      ? this.host.paymentsService.getTotalWithAdvancePayment(
          this.host.missingPaid(),
          calculateAvailable(payment.value, payment.value_used),
        )
      : 0;
  }

  get bankOrCashierOptions(): SelectOption[] {
    const isCash = this.host.paymentMethodControl.value === 1;
    const list = isCash ? this.host.listCashiers() : this.host.listBanks();

    return list.map((option) => ({
      value: option.id_payment,
      label: option.description,
    }));
  }

  get paymentMethodOptions(): SelectOption[] {
    return PAYMENT_METHODS.map((option) => ({
      value: option.value,
      label: this.host.translate.instant(option.viewValue),
    }));
  }
}
